/* eslint-disable no-unused-vars */
import React, {useCallback, useContext, useEffect, useState, useRef} from 'react';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AlertsApi, LinesApi, SharedServicesApi} from '../../core/api';
import {ServicesApi} from '../../core/api/services.api';
import {
    SHARED_SERVICES_GROUP_SLUGS,
    TRANSPORT_GROUP_ID,
    TRANSPORT_GROUP_KEY_MAP,
} from '../../core/constants/transport.constants';
import {Db} from '../../core/data/db';
import {RealTimes, RealTimeWorker} from '../../core/data/real-time-worker';
import {AlertModel, RouteModel, ServiceModel, StopModel, TripCoords} from '../../core/models';
import {PinModel} from '../../core/models/pin.model';
import {createPin, filterBySlug, getTripCoords, createEntryPin} from '../../core/utils';
import {SpinnerContext} from '../spinner/spinner.context';
import {DataProvider} from './data.context';
import {formatServiceCatalog, plainDocument, plainDocumentIconLine} from '../../utils/utils';
import {TRANSPORT_GROUPS} from '../../core/constants';
import {useLocation} from 'react-router-dom';

const LAST_SYNC_KEY = 'lastSync';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const DataService = (props: any) => {
    const location = useLocation();

    const {activeSpinner, deactivateSpinner} = useContext(SpinnerContext);

    const [db, setDb] = useState<Db>();
    const [ready, setReady] = useState<boolean>(false);

    const [realTimeWorker, setRealTimeWorker] = useState<RealTimeWorker>();

    const [currentStop, setCurrentStop] = useState<StopModel | null>(null);
    const [currentStopAlerts, setCurrentStopAlerts] = useState<AlertModel[]>([]);
    const [currentStopEntries, setCurrentStopEntries] = useState<StopModel[]>([]);

    const [visibleStops, setVisibleStops] = useState<StopModel[]>([]);
    const [closeStops, setCloseStops] = useState<StopModel[]>([]);

    const [currentRoute, setCurrentRoute] = useState<RouteModel | null>(null);
    const [currentRouteAlerts, setCurrentRouteAlerts] = useState<AlertModel[]>([]);

    const [currentTrip, setCurrentTrip] = useState<number>(0);
    const [tripCoords, setTripCoords] = useState<TripCoords[]>([]);

    const [pinSet, setPinSet] = useState<{ [slug: string]: PinModel } | null>(null);
    const [services, setServices] = useState<ServiceModel[]>([]);

    const [realTimes, setRealTimes] = useState<RealTimes>({});

    const unsubscribe$: Subject<boolean> = new Subject<boolean>();

    const [slugFilterState, setSlugFilterState] = useState<Record<string, boolean>>({});
    const loadedSharedServices = useRef<Set<string>>(new Set());

    const init = useCallback(async (): Promise<Db> => {

        activeSpinner();

        let coreDb = db;
        if (!coreDb) {

            coreDb = new Db();
            await coreDb.init();

            setDb(coreDb);

            setReady(true);
        }

        await initMainData();

        if (!realTimeWorker) {
            const realTimeWorker = new RealTimeWorker(coreDb);
            setRealTimeWorker(realTimeWorker);
        }

        deactivateSpinner();

        return coreDb;

    }, [db, realTimeWorker]);

    const initMainData = useCallback(async () => {
        await Promise.all([
            getPinSet(),
            getServices(),
        ]);
    }, [db]);

    const sync = async (db: Db) => {
        const lastSync = Number(localStorage.getItem(LAST_SYNC_KEY)) || 0;
        const now = Date.now();

        if (now - lastSync < ONE_DAY_MS) {
            await initMainData();
            return;
        }

        const pins = await db.dataSources?.pins.find();
        const services = await db.dataSources?.services.find();
        const stops = await db.dataSources?.stops.find();

        const [servicesData, alerts, lines] = await Promise.all([
            ServicesApi.getServices(),
            AlertsApi.getAlerts(),
            LinesApi.getLines(),
        ]);

        const pinsToInsert = await Promise.all(
            servicesData.services.map((service) => getPin(service)),
        );

        const stopsToInsert = (
            await Promise.all(
                servicesData.services.map((service) => getStops(db, service)),
            )
        ).flat();

        pinsToInsert.push({
            id: 'entry',
            color: '',
            colorDark: '',
            icon: '',
            iconDark: '',
            logo: '',
            logoDark: '',
            pin: await createEntryPin() || '',
        });

        await Promise.all([
            db.dataSources?.pins.bulkInsert(pinsToInsert),
            db.dataSources?.services.bulkInsert(servicesData.services),
            db.dataSources?.serviceGroups.bulkInsert(servicesData.serviceGroups),
            db.dataSources?.alerts.bulkInsert(alerts),
            db.dataSources?.routes.bulkInsert(lines.routes),
            db.dataSources?.stops.bulkInsert([...stopsToInsert, ...lines.stops]),
        ]);

        localStorage.setItem(LAST_SYNC_KEY, now.toString());
        await initMainData();
    };

    const getPin = async (service: ServiceModel) => {
        const [icon, iconDark, logo, logoDark] = await Promise.all([
            ServicesApi.getAsset(service.icon.id),
            ServicesApi.getAsset(service.iconDark.id),
            ServicesApi.getAsset(service.logo.id),
            ServicesApi.getAsset(service.logoDark.id),
        ]);

        return {
            id: service.slug,
            color: service.color,
            colorDark: service.colorDark,
            icon,
            iconDark,
            logo,
            logoDark,
            pin: service.icon.id ? await createPin(icon, service.color) : '',
        };
    };

    const getStops = async (db: Db, service: ServiceModel, forceCall = false) => {
        const isSharedService = SHARED_SERVICES_GROUP_SLUGS.includes(service.groupSlug);

        if (isSharedService && !forceCall && !slugFilterState[service.slug]) {
            return [];
        }

        const [currentStops, newStops] = await Promise.all([
            db.dataSources?.stops.find({selector: {slug: service.slug}}) || Promise.resolve([]),
            isSharedService ? SharedServicesApi.getStops(service) : Promise.resolve([]),
        ]);


        const stopsToUse = !newStops || !newStops.length ? currentStops : newStops;
        if (stopsToUse.length > 0) {
            await db.dataSources?.stops.newBulkInsertWithOutDeleteOld(stopsToUse);
            await db?.dataSources?.stops.setBounds();
        }

        return stopsToUse;
    };

    useEffect(() => {
        if (!db) {
            return;
        }

        const activeSharedServices = services.filter(
            (service: any) =>
                slugFilterState[service.slug] &&
                SHARED_SERVICES_GROUP_SLUGS.includes(service.groupSlug));

        if (activeSharedServices.length === 0) return;


        activeSharedServices.forEach((service: any) => {
            if (!loadedSharedServices.current.has(service.slug)) {
                loadedSharedServices.current.add(service.slug);

                const fullService = services.find((s: any) => s.slug === service.slug);
                if (fullService) {
                    getStops(db, fullService);
                }
            }
        });
    }, [slugFilterState, services, db]);


    async function handleNearbyServices(serviceId: number) {
        if (!db) return;
        const serviceGroup = TRANSPORT_GROUPS.find((group) => group.id === serviceId);
        if (!serviceGroup) return;

        const allServices = await db?.dataSources?.services.find() || [];

        const nearbySharedServices = allServices.filter((service: any) =>
            serviceGroup.slugs.includes(service.slug) && SHARED_SERVICES_GROUP_SLUGS.includes(service.groupSlug));

        for (const service of nearbySharedServices) {
            if (!loadedSharedServices.current.has(service.slug)) {
                loadedSharedServices.current.add(service.slug);
                await getStops(db, service, true);
            }
        }
    };

    async function getStatusSlugFilter(statusSlugFilter: any) {
        setSlugFilterState((prevState) => ({
            ...prevState,
            ...statusSlugFilter,
        }));
    }

    const getPinSet = useCallback(async () => {
        setPinSet(await db?.dataSources?.pins.getPinSet() || null);
    }, [db]);

    const getServices = useCallback(async () => {
        setServices(await db?.dataSources?.services.find() || []);
    }, [db]);

    async function getAllServiceCatalogFormated(): Promise<any> {
        return db?.dataSources?.serviceGroups
            .find();
        // .then(plainDocument)
        // .then(formatServiceCatalog);
    };

    async function getLinesById(query: number):Promise<any> {
        console.log('routes', db?.dataSources?.routes);
        // return db?.dataSources?.routes
        //     .find()
        //     .where('route.route_id').eq(query)
        //     .exec()
        //     .then(plainDocumentIconLine);
    };

    const currentRouteHandler = useCallback(async () => {

        activeSpinner();

        setCurrentTrip(0);

        if (currentRoute && db) {
            setTripCoords(await getTripCoords(currentRoute, db));
        } else {
            setTripCoords([]);
        }

        await getCurrentRouteAlerts();

        deactivateSpinner();
    }, [db, currentRoute]);

    const currentStopHandler = useCallback(async () => {
        activeSpinner();
        await getCurrentStopAlerts();
        setCurrentStopEntries(currentStop && currentStop.slug === 'metro' ?
            await db?.dataSources?.stops.find({selector: {parentStation: currentStop?.stopId}}) || [] :
            []);
        if (realTimeWorker) realTimeWorker.currentStop = currentStop;
        deactivateSpinner();
    }, [db, currentStop, realTimeWorker]);

    const servicesHandler = useCallback(() => {

        if (realTimeWorker) {
            realTimeWorker.sharedServices = filterBySlug(services, [
                ...(TRANSPORT_GROUP_KEY_MAP[TRANSPORT_GROUP_ID.BIKE]?.slugs || []),
                ...(TRANSPORT_GROUP_KEY_MAP[TRANSPORT_GROUP_ID.OTHER]?.slugs || []),
            ]);
        }
    }, [realTimeWorker, services]);

    const getCurrentStop = useCallback(async (id?: string) => {
        if (id) {
            setCurrentStop(await db?.dataSources?.stops.findOneById(id) || null);
        } else {
            setCurrentStop(null);
        }
    }, [db]);

    const getCurrentStopAlerts = useCallback(async () => {
        if (currentStop) {
            try {
                const allAlerts = await db?.dataSources?.alerts.find() || [];
                const filteredAlerts = allAlerts.filter((alert: any) => {
                    const matchesStopId = alert.alertStop.includes(currentStop.stopId);
                    const matchesRouteId = currentStop.routesIds.some((routeId) => alert.alertRoute.includes(routeId));
                    return matchesStopId || matchesRouteId;
                });
                setCurrentStopAlerts(filteredAlerts);
            } catch (error) {
                console.error('Error fetching alerts:', error);
                setCurrentStopAlerts([]);
            }
        } else {
            setCurrentStopAlerts([]);
        }
    }, [currentStop]);

    const getCurrentRoute = useCallback(async (id?: string) => {
        if (id) {
            setCurrentRoute(await db?.dataSources?.routes.findOneById(id) || null);
        } else {
            setCurrentRoute(null);
        }
    }, [db]);

    const getCurrentRouteAlerts = useCallback(async () => {
        if (currentRoute) {
            setCurrentRouteAlerts(await db?.dataSources?.alerts.find({
                selector: {
                    alertRoute: currentRoute.routeId,
                },
            }) || []);
        } else {
            setCurrentRouteAlerts([]);
        }
    }, [currentRoute]);

    // async function insertSearch(search: any):Promise<any> {
    //     db?.dataSources?.recent_searches.insert(search);
    // }

    async function removeSearch(value: any, field: string):Promise<any> {
        db?.dataSources?.searches
            .find();
        // .where(field).eq(value)
        // .remove();
    }

    async function updateSearch(search: any, field: string, value: any):Promise<any> {
        await db?.dataSources?.searches
            .find();
        // .where(field).eq(search[field])
        // .update({
        //     $set: {
        //         [field]: value,
        //     },
        // });
    }

    async function getAllRecentSearches():Promise<any> {
        console.log('DSOURCE', db?.dataSources?.searches);
        // return db?.dataSources?.recent_searches
        //     .find()
        //     .sort({datetime: 'desc'})
        //     .exec()
        //     .then((documents)=>{
        //         return documents.map((document)=>{
        //             return document.toJSON();
        //         });
        //     });
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (!db || !realTimeWorker) return;
        let called = false;

        (async () => {
            if (!called) {
                called = true;
                await sync(db);
            }

            db.dataSources?.stops.visibleStops$
                .pipe(
                    takeUntil(unsubscribe$),
                )
                .subscribe(setVisibleStops);

            db.dataSources?.stops.closeStops$
                .pipe(
                    takeUntil(unsubscribe$),
                )
                .subscribe(setCloseStops);

            realTimeWorker.realTimes$
                .pipe(
                    takeUntil(unsubscribe$),
                )
                .subscribe(setRealTimes);
        })();
        return () => {
            unsubscribe$.next(true);
            unsubscribe$.complete();
        };
    }, [db, realTimeWorker]);

    useEffect(() => {
        currentStopHandler();
    }, [currentStop]);

    useEffect(() => {
        currentRouteHandler();
    }, [currentRoute]);

    useEffect(() => {
        if (realTimeWorker) realTimeWorker.closeStops = closeStops;
    }, [closeStops]);

    useEffect(() => {
        if (location.pathname === '/home') {
            setCloseStops([]);
            if (realTimeWorker) realTimeWorker.closeStops = [];
        }
    }, [location.pathname]);

    useEffect(() => {
        servicesHandler();
    }, [services]);

    return <DataProvider value={{
        db,
        ready,
        init,
        pinSet,
        services,
        getCurrentStop,
        getAllServiceCatalogFormated,
        handleNearbyServices,
        getStatusSlugFilter,
        getLinesById,
        currentStop,
        currentStopAlerts,
        currentStopEntries,
        visibleStops,
        closeStops,
        getCurrentRoute,
        currentRoute,
        currentRouteAlerts,
        currentTrip,
        setCurrentTrip,
        tripCoords,
        realTimes,
        realTimeWorker,
        getAllRecentSearches,
    }}>
        {props.children}
    </DataProvider>;
};
